import React from 'react';
import { useTranslation } from 'react-i18next';

import Tracked from '~/components/Tracked';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import { brandLogos } from '../brandLogos';
import MenuLink from '../MenuLink';
import type { MenuItemProps } from '../types';
import MegaNavCategoryList from './MegaNavCategoryList';

type FeaturedBrandModel = {
  name: string;
  slug: string;
};

type FeaturedBrand = {
  children?: FeaturedBrandModel[];
  icon?: string;
  name: string;
  path: string;
  viewAll?: {
    label: string;
    link: string;
  };
};

export default function MegaNavItem({
  link,
  name,
  content,
  slug,
  track,
  pagePosition,
  position = 1
}: MenuItemProps) {
  const { t } = useTranslation();
  const fireNavigationLink = useNavigationLinkInteraction('meganav');
  const featuredBrandsLabel = t('featuredBrands');
  const viewAllBrandsLabel = t('viewAllBrands');

  return (
    <li className="group flex h-[50px] grow justify-center whitespace-nowrap">
      <Tracked.ExternalLink
        name={name as TrackedButtonClickName}
        href={link}
        variant="none"
        className="flex w-full shrink-0 items-center justify-center px-[10px] py-4 font-heading text-[15px] font-medium tracking-[0.4px] text-white hover:rounded-md hover:bg-grey-dark max-[1199.98px]:w-fit"
        pagePosition="Header"
        onClick={() =>
          fireNavigationLink(link, position, {
            ancestorLabels: [],
            label: name
          })
        }
      >
        {name}
      </Tracked.ExternalLink>
      {content?.render ? (
        <div className="absolute left-0 top-[60px] hidden max-h-[calc(100vh-60px)] w-full overflow-y-hidden shadow-[0px_5px_5px_-3px_rgba(0,0,0,0.18)] group-hover:block">
          <div className="h-[10px]"></div>
          <div className="max-h-[calc(100vh-70px)] w-full overflow-auto bg-white">
            <div className="content-width max-h-[calc(100vh-65px)] pb-10 pt-8">
              <div className="flex w-full flex-col">
                {content?.footwear?.filters.length > 0 && (
                  <div className="flex w-full gap-6">
                    <div className="text-title-sm">
                      {content?.footwear?.title}
                    </div>
                    <div className="flex gap-4">
                      {content?.footwear?.filters.map(
                        (item: MenuItemProps, i: number) => (
                          <React.Fragment key={`${item?.title}/${item?.link}`}>
                            <MenuLink
                              title={item?.title}
                              link={item?.link}
                              track={track}
                              pagePosition={pagePosition}
                              onClick={() =>
                                fireNavigationLink(item?.link, i + 1, {
                                  ancestorLabels: [name],
                                  label: item?.title
                                })
                              }
                            />
                          </React.Fragment>
                        )
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-4 mt-6 flex items-center gap-6">
                  <div className="text-title-sm">{featuredBrandsLabel}</div>
                  {content?.footwear?.viewAll && (
                    <MenuLink
                      link={content?.footwear.viewAll.link}
                      title={viewAllBrandsLabel}
                      onClick={() =>
                        fireNavigationLink(content?.footwear.viewAll.link, 1, {
                          ancestorLabels: [name, featuredBrandsLabel],
                          label: viewAllBrandsLabel
                        })
                      }
                    />
                  )}
                </div>

                {content?.footwear?.featuredBrands && (
                  <div
                    className={`flex w-full justify-between ${
                      content?.footwear?.featuredBrands.path === 'clothing'
                        ? 'mr-10'
                        : ''
                    }`}
                  >
                    {content?.footwear?.featuredBrands.children.map(
                      (item: FeaturedBrand, i: number) => {
                        const {
                          children,
                          icon,
                          name: brandName,
                          viewAll,
                          path
                        } = item;
                        const brandHref = viewAll
                          ? `${path}${viewAll?.link || ''}`
                          : undefined;

                        return (
                          <div key={i} className="flex flex-col">
                            {icon ? (
                              <Tracked.ExternalLink
                                variant="none"
                                href={brandHref}
                                pagePosition="Header"
                                name={brandName as TrackedButtonClickName}
                                onClick={() =>
                                  brandHref &&
                                  fireNavigationLink(brandHref, i + 1, {
                                    ancestorLabels: [name, featuredBrandsLabel],
                                    label: brandName
                                  })
                                }
                              >
                                <img
                                  alt={brandName}
                                  src={brandLogos[icon]}
                                  loading="lazy"
                                  className="h-12"
                                />
                              </Tracked.ExternalLink>
                            ) : (
                              slug
                            )}
                            {children && (
                              <>
                                {viewAll && (
                                  <Tracked.ExternalLink
                                    href={`${path}${viewAll?.link || ''}`}
                                    className="text-title-xs flex h-6 items-center pt-1"
                                    variant="none"
                                    pagePosition="Header"
                                    name={
                                      viewAll.label as TrackedButtonClickName
                                    }
                                    onClick={() =>
                                      fireNavigationLink(
                                        `${path}${viewAll?.link || ''}`,
                                        1,
                                        {
                                          ancestorLabels: [
                                            name,
                                            featuredBrandsLabel,
                                            brandName
                                          ],
                                          label: viewAll.label
                                        }
                                      )
                                    }
                                  >
                                    {viewAll.label}
                                  </Tracked.ExternalLink>
                                )}
                                {children?.map(
                                  (brand: FeaturedBrandModel, j: number) => (
                                    <Tracked.ExternalLink
                                      className="h-[25px] pt-[3px] text-[13px] font-normal text-grey-read"
                                      key={`sub_brand_${j}`}
                                      href={`${path}${brand?.slug}/`}
                                      pagePosition="Header"
                                      variant="none"
                                      name={
                                        brand.name as TrackedButtonClickName
                                      }
                                      onClick={() =>
                                        fireNavigationLink(
                                          `${path}${brand?.slug}/`,
                                          j + (viewAll ? 2 : 1),
                                          {
                                            ancestorLabels: [
                                              name,
                                              featuredBrandsLabel,
                                              brandName
                                            ],
                                            label: brand?.name
                                          }
                                        )
                                      }
                                    >
                                      {brand?.name}
                                    </Tracked.ExternalLink>
                                  )
                                )}
                              </>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
                {content?.footwear?.categories && (
                  <div className="mt-[17px] grid grid-cols-[1fr_1fr_1fr_3fr]">
                    {['sport', 'style', 'material', 'colour'].map(
                      (category) =>
                        content?.footwear?.categories?.[category] && (
                          <MegaNavCategoryList
                            key={category}
                            title={content.footwear.categories[category].title}
                            parentLink={
                              content.footwear.categories[category].link
                            }
                            ancestorLabels={[
                              name,
                              content.footwear.categories[category].title
                            ]}
                          >
                            {content.footwear.categories[category].children}
                          </MegaNavCategoryList>
                        )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </li>
  );
}
